import type { Href } from "expo-router";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, TextInput, useWindowDimensions, View } from "react-native";

import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Circle, Path } from "react-native-svg";

import { BrandScene } from "../../../../components/student/BrandScene";
import { clearProgramEntranceFlags } from "../../../../components/student/ProgramEntrance";
import { StudentPortalShell } from "../../../../components/student/StudentPortalShell";
import { EmptyState, Skeleton } from "../../../../components/ui";
import {
  competitionTier,
  competitionTierLabel,
  type CompetitionTier,
} from "../../../../lib/studentCases";
import { formatDateShort } from "../../../../lib/datetime";
import { loadStudentCasesPageData } from "../../../../lib/studentDashboard";
import { useStudentPortalShellData } from "../../../../lib/useStudentPortalShellData";
import type { StudentCaseRow } from "../../../../types/database";


type TierKey = "all" | CompetitionTier;

const TIER_TABS: Array<{ key: TierKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "campus", label: "Campus" },
  { key: "regional", label: "Regional" },
  { key: "national", label: "National" },
];

/**
 * v2 Discover (Find a competition) — editorial hero + horizontal tier
 * tabs + featured-card + 2-column grid. Replaces the prior left-rail
 * layout. Data still flows through `loadStudentCasesPageData` so all
 * eligibility / registration / tier rules stay consistent with the
 * dashboard's "My programs" surface.
 */
export default function FindCompetitionScreen() {
  const shell = useStudentPortalShellData();
  const { width } = useWindowDimensions();
  const wide = width >= 900;

  const [tier, setTier] = useState<TierKey>("all");
  const [query, setQuery] = useState("");
  const [allActive, setAllActive] = useState<StudentCaseRow[]>([]);
  const [allSoon, setAllSoon] = useState<StudentCaseRow[]>([]);
  const [myActiveIds, setMyActiveIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const studentId = shell.student?.id ?? null;

  const reload = useCallback(async () => {
    if (!studentId) {
      setAllActive([]);
      setAllSoon([]);
      setMyActiveIds(new Set());
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { active, soon, myActive } = await loadStudentCasesPageData(
        shell.student,
        studentId
      );
      setAllActive(active);
      setAllSoon(soon);
      setMyActiveIds(new Set(myActive.map((r) => r.id)));
    } finally {
      setLoading(false);
    }
  }, [studentId, shell.student]);

  useEffect(() => {
    void reload();
    // Visiting Find means the student left whatever program they were
    // in — wipe the per-program entrance flags so the next program
    // they open replays the loading cover.
    clearProgramEntranceFlags();
  }, [reload]);

  const tierCounts = useMemo(() => {
    const all = [...allActive, ...allSoon];
    return TIER_TABS.reduce<Record<TierKey, number>>(
      (acc, t) => {
        acc[t.key] =
          t.key === "all"
            ? all.length
            : all.filter((r) => competitionTier(r) === t.key).length;
        return acc;
      },
      { all: 0, campus: 0, regional: 0, national: 0 }
    );
  }, [allActive, allSoon]);

  // Tier filter first, then text search across name + company.
  const visibleRows = useMemo(() => {
    const all = [...allActive, ...allSoon];
    const tiered = tier === "all" ? all : all.filter((r) => competitionTier(r) === tier);
    const q = query.trim().toLowerCase();
    if (!q) return tiered;
    return tiered.filter(
      (r) =>
        (r.name ?? "").toLowerCase().includes(q) ||
        (r.company_name ?? "").toLowerCase().includes(q)
    );
  }, [allActive, allSoon, tier, query]);

  const featured = visibleRows[0] ?? null;
  const others = featured ? visibleRows.slice(1) : visibleRows;

  // Hero kicker — just a live count line, no season prefix. The earlier
  // "Spring '26 · N case competitions…" version meant the kicker had to
  // be retuned every academic quarter; the count alone keeps the hero
  // honest year-round without anyone needing to remember to update it.
  const kicker = useMemo(() => {
    const total = allActive.length + allSoon.length;
    if (total === 0) return "Discover";
    return `${total} case competition${total === 1 ? "" : "s"} open to your team`;
  }, [allActive, allSoon]);

  return (
    <StudentPortalShell
      topbarTitle="Find a competition"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        style={{ backgroundColor: COLORS.FOREST.hex }}
        contentContainerStyle={{ paddingBottom: 80, flexGrow: 1 }}
      >
        {/*
          Per-section BrandScene (matches the welcome page pattern).
          Each section renders a scene as its first child, content
          follows in DOM order — content paints on top naturally
          without any zIndex trickery. The Find page uses the
          `vines` scene; welcome uses `palms`. See
          components/student/BrandScene.tsx for the scene library.
        */}
        <Hero kicker={kicker} query={query} onQuery={setQuery} />

        {/* Forest-green section below the hero seam. BrandScene
            renders first; content following in JSX paints over it. */}
        <View
          style={{
            backgroundColor: COLORS.FOREST.hex,
            flexGrow: 1,
            minHeight: 600,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <BrandScene scene="vines" tone="on-green" />
          <View
            className="max-w-[1280px] px-5 pt-2 md:px-11"
            style={{
              width: "100%",
              alignSelf: "center",
            }}
          >
            <TierTabStrip tier={tier} counts={tierCounts} onChange={setTier} />

            {loading ? (
              <SkeletonGrid wide={wide} />
            ) : visibleRows.length === 0 ? (
              <View
                style={{
                  backgroundColor: COLORS.LINEN.hex,
                  borderWidth: 1,
                  borderColor: STUDENT_THEME.border,
                  borderRadius: 8,
                  paddingVertical: 56,
                  paddingHorizontal: 24,
                }}
              >
                <EmptyState
                  title="No matches"
                  subtitle={
                    query.trim()
                      ? "Try a different search or switch tiers."
                      : `No ${tier === "all" ? "open" : tier} programs right now — check back soon.`
                  }
                />
              </View>
            ) : (
              <>
                {featured ? (
                  <FeaturedCard
                    row={featured}
                    joined={myActiveIds.has(featured.id)}
                    wide={wide}
                  />
                ) : null}

                {others.length > 0 ? (
                  <View style={{ marginTop: featured ? 32 : 0 }}>
                    <SectionLabel title="All programs" count={others.length} />
                    <View
                      style={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 14,
                      }}
                    >
                      {others.map((row) => (
                        <View
                          key={row.id}
                          style={{
                            flexBasis: 0,
                            flexGrow: 1,
                            flexShrink: 1,
                            minWidth: wide ? 320 : 280,
                            width: "100%" as unknown as number,
                          }}
                        >
                          <ProgramCard row={row} joined={myActiveIds.has(row.id)} />
                        </View>
                      ))}
                    </View>
                  </View>
                ) : null}
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </StudentPortalShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────────────────────────────────────

function Hero({
  kicker,
  query,
  onQuery,
}: {
  kicker: string;
  query: string;
  onQuery: (next: string) => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <View
      style={{
        paddingHorizontal: 24,
        paddingTop: 22,
        paddingBottom: 28,
        // Explicit sand bg so the Hero stays beige now that the
        // ScrollView's page-level bg is forest green.
        backgroundColor: COLORS.SAND.hex,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.SAND_DARK.hex,
        position: "relative",
        // overflow:hidden clips the BrandScene to the section bounds.
        // The vines scene intentionally treats the seam as a horizon
        // (not a join), so per-section clipping is the desired behavior.
        overflow: "hidden",
      }}
      className="md:px-11"
    >
      {/* Decorative palm branches — sage on cream. First child so
          content rendered after this in JSX paints on top naturally
          via DOM order. Matches the welcome page's pattern. */}
      <BrandScene scene="vines" tone="on-beige" />
      <View
        pointerEvents="none"
        style={[
          { position: "absolute", left: 24, right: 24, bottom: -1, height: 1, zIndex: 3 },
          isWeb
            ? ({
                backgroundImage:
                  "linear-gradient(90deg, transparent, rgba(181,135,58,0.45), transparent)",
              } as object)
            : { backgroundColor: STUDENT_THEME.accentBorderSoft },
        ]}
      />

      <BackToHome />

      <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <View style={{ height: 1, width: 28, backgroundColor: COLORS.AMBER.hex }} />
        <Text
          numberOfLines={1}
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: COLORS.AMBER.hex,
          }}
        >
          {kicker}
        </Text>
      </View>

      <Text
        style={{
          fontFamily: FONTS.display,
          fontSize: 48,
          lineHeight: 50,
          letterSpacing: -0.6,
          color: COLORS.FOREST.hex,
          marginBottom: 8,
        }}
      >
        Find your next{" "}
        <Text
          style={{
            fontFamily: FONTS.display,
            fontStyle: "italic",
            color: COLORS.AMBER.hex,
          }}
        >
          case
        </Text>
        .
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13.5,
          lineHeight: 21,
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: 18,
          maxWidth: 600,
        }}
      >
        Coravu surfaces case competitions matched to your school&apos;s tier and your team&apos;s
        eligibility. Search by sponsor or program name, or browse by tier.
      </Text>

      {/* Search row — centered within the hero. Renders after
          BrandScene in JSX, so it paints over the branches via
          DOM order; no zIndex needed. */}
      <View
        style={{
          flexDirection: "row",
          gap: 10,
          alignItems: "center",
          maxWidth: 720,
          width: "100%" as unknown as number,
          marginHorizontal: "auto",
          alignSelf: "center",
        }}
      >
        <View
          style={{
            flex: 1,
            position: "relative",
            backgroundColor: STUDENT_THEME.surfaceRaisedBg,
            borderRadius: 5,
            borderWidth: 1,
            borderColor: COLORS.SAND_DARK.hex,
            height: 46,
            justifyContent: "center",
          }}
        >
          <View style={{ position: "absolute", left: 14, top: 15 }} pointerEvents="none">
            <SearchGlyph />
          </View>
          <TextInput
            value={query}
            onChangeText={onQuery}
            placeholder="Search by program, sponsor, or track…"
            placeholderTextColor={STUDENT_THEME.textOnSurfaceFaint}
            style={[
              {
                paddingLeft: 40,
                paddingRight: 14,
                fontSize: 14,
                fontFamily: FONTS.body,
                color: COLORS.FOREST.hex,
                height: 46,
                width: "100%",
              },
              isWeb ? ({ outlineStyle: "none" } as unknown as object) : null,
            ]}
          />
        </View>
      </View>
    </View>
  );
}

function BackToHome() {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={() => router.push("/student" as Href)}
      accessibilityRole="button"
      accessibilityLabel="Back to home"
      {...(isWeb
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
        : {})}
      style={[
        {
          alignSelf: "flex-start",
          flexDirection: "row",
          alignItems: "center",
          gap: 7,
          paddingVertical: 4,
          marginBottom: 14,
        },
        isWeb ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Svg width={11} height={11} viewBox="0 0 10 10" fill="none">
        <Path
          d="M8 5H2M4.5 2.5L2 5l2.5 2.5"
          stroke={COLORS.AMBER.hex}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
      <Text
        style={{
          fontFamily: FONTS.bodySemiBold,
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color: COLORS.AMBER.hex,
          opacity: hover && isWeb ? 0.85 : 1,
        }}
      >
        Back to home
      </Text>
    </Pressable>
  );
}

function SearchGlyph() {
  return (
    <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
      <Circle cx={7} cy={7} r={4.5} stroke={STUDENT_THEME.textOnSurfaceFaint} strokeWidth={1.4} />
      <Path d="M10.5 10.5 13.5 13.5" stroke={STUDENT_THEME.textOnSurfaceFaint} strokeWidth={1.4} strokeLinecap="round" />
    </Svg>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tier tab strip
// ────────────────────────────────────────────────────────────────────────────

function TierTabStrip({
  tier,
  counts,
  onChange,
}: {
  tier: TierKey;
  counts: Record<TierKey, number>;
  onChange: (next: TierKey) => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 4,
        borderBottomWidth: 1,
        // Cream-tinted divider so it reads on the forest section bg.
        borderBottomColor: "rgba(247,244,238,0.12)",
        marginBottom: 28,
        marginTop: 6,
        flexWrap: "wrap",
      }}
    >
      {TIER_TABS.map((t) => (
        <TierTab
          key={t.key}
          label={t.label}
          count={counts[t.key]}
          active={tier === t.key}
          onPress={() => onChange(t.key)}
        />
      ))}
    </View>
  );
}

function TierTab({
  label,
  count,
  active,
  onPress,
}: {
  label: string;
  count: number;
  active: boolean;
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      accessibilityLabel={`${label} (${count})`}
      style={({ hovered }) => [
        {
          paddingVertical: 12,
          paddingHorizontal: 16,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          borderBottomWidth: 2,
          borderBottomColor: active ? COLORS.AMBER.hex : "transparent",
          marginBottom: -1,
        },
        isWeb ? ({ cursor: "pointer", transition: "color 0.15s ease" } as object) : null,
        isWeb && hovered && !active ? { opacity: 0.85 } : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodySemiBold,
          fontSize: 12,
          letterSpacing: 0.4,
          // Cream-on-forest now — TierTabStrip sits directly on the
          // green section bg below the Hero divider.
          color: active ? COLORS.LINEN.hex : "rgba(247,244,238,0.62)",
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 11,
          color: active ? COLORS.AMBER.hex : STUDENT_THEME.textOnSidebarIdle,
          fontVariant: ["tabular-nums"],
          opacity: 0.85,
        }}
      >
        {count}
      </Text>
    </Pressable>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Featured card — split forest/white, "At a glance" key/value list on right
// ────────────────────────────────────────────────────────────────────────────

function FeaturedCard({
  row,
  joined,
  wide,
}: {
  row: StudentCaseRow;
  joined: boolean;
  wide: boolean;
}) {
  const isWeb = Platform.OS === "web";
  const synopsis =
    row.short_description?.trim() ||
    row.brief?.split(/\r?\n/)[0]?.trim() ||
    "A new case competition on Coravu.";
  const tier = competitionTier(row);
  const teamSize =
    row.team_size_min === row.max_team_size
      ? `${row.max_team_size}`
      : `${row.team_size_min}–${row.max_team_size}`;
  const statusLabel = (() => {
    if (row.status === "open") return "Registration open";
    if (row.status === "judging") return "Judging";
    if (row.status === "closed") return "Closed";
    return "Opens soon";
  })();

  const glance: { k: string; v: string }[] = [
    { k: "Track", v: competitionTierLabel(tier) },
    { k: "Closes", v: row.closes_at ? formatDateShort(row.closes_at) : "TBD" },
    { k: "Teams", v: teamSize },
    { k: "Status", v: statusLabel },
  ];

  const onApply = () =>
    router.push(`/student/find/competition/${row.id}` as Href);

  return (
    <View
      style={[
        {
          flexDirection: wide ? "row" : "column",
          backgroundColor: COLORS.LINEN.hex,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: STUDENT_THEME.border,
          overflow: "hidden",
          marginBottom: 12,
        },
        isWeb
          ? ({
              boxShadow: "0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.10)",
            } as object)
          : null,
      ]}
    >
      {/* Forest left side */}
      <View
        style={[
          {
            flex: wide ? 1.4 : undefined,
            paddingVertical: 32,
            paddingHorizontal: 32,
            backgroundColor: COLORS.FOREST.hex,
            position: "relative",
            overflow: "hidden",
          },
          isWeb
            ? ({
                backgroundImage:
                  "radial-gradient(ellipse 500px 300px at 90% 10%, rgba(181,135,58,0.20) 0%, transparent 60%)",
              } as object)
            : null,
        ]}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <View style={{ height: 1, width: 28, backgroundColor: COLORS.AMBER.hex }} />
          <Text
            numberOfLines={1}
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 2.2,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
            }}
          >
            Featured · {row.company_name || "Sponsor"}
          </Text>
        </View>
        <Text
          style={{
            fontFamily: FONTS.display,
            fontSize: 34,
            lineHeight: 38,
            letterSpacing: -0.3,
            color: COLORS.LINEN.hex,
            marginBottom: 12,
          }}
          numberOfLines={2}
        >
          {row.name}
        </Text>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 14,
            lineHeight: 22,
            color: "rgba(247,244,238,0.72)",
            marginBottom: 22,
            maxWidth: 460,
          }}
          numberOfLines={3}
        >
          {synopsis}
        </Text>

        <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
          <ApplyButton joined={joined} onPress={onApply} />
          <SecondaryDarkButton label="Read brief" onPress={onApply} />
        </View>
      </View>

      {/* Cream right side ("At a glance" panel) */}
      <View
        style={{
          flex: 1,
          paddingVertical: 32,
          paddingHorizontal: 32,
          backgroundColor: COLORS.LINEN.hex,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 2.1,
            textTransform: "uppercase",
            color: STUDENT_THEME.textOnSurfaceFaint,
            marginBottom: 16,
          }}
        >
          At a glance
        </Text>
        {glance.map((r, i) => (
          <View
            key={r.k}
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingVertical: 12,
              borderBottomWidth: i === glance.length - 1 ? 0 : 1,
              borderBottomColor: COLORS.SAND_LINE.hex,
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 10,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: STUDENT_THEME.textOnSurfaceFaint,
              }}
            >
              {r.k}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.bodyMedium,
                fontSize: 13,
                color: COLORS.FOREST.hex,
              }}
            >
              {r.v}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function ApplyButton({ joined, onPress }: { joined: boolean; onPress: () => void }) {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);
  // Pre-registration CTA reads "Register" to match the verb used across
  // the rest of the student portal (registrations.ts, the workspace
  // fallback button, etc.) — "Apply" implied a competitive review step
  // and "Join" overlapped with the team-membership UI.
  const label = joined ? "View program" : "Register";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      {...(isWeb
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
        : {})}
      style={[
        {
          paddingVertical: 11,
          paddingHorizontal: 18,
          borderRadius: 5,
          backgroundColor: hover && isWeb ? COLORS.AMBER_WARM.hex : COLORS.AMBER.hex,
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition: "background-color 0.18s ease, transform 0.18s ease",
              transform: [{ translateY: hover ? -1 : 0 }],
            } as object)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 11,
          letterSpacing: 1.76,
          textTransform: "uppercase",
          color: COLORS.FOREST.hex,
        }}
      >
        {label}
      </Text>
      <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
        <Path
          d="M2.5 6h7M6.5 3l3 3-3 3"
          stroke={COLORS.FOREST.hex}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
    </Pressable>
  );
}

function SecondaryDarkButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      {...(isWeb
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
        : {})}
      style={[
        {
          paddingVertical: 11,
          paddingHorizontal: 18,
          borderRadius: 5,
          borderWidth: 1,
          borderColor: hover && isWeb ? STUDENT_THEME.textOnSidebarDim : "rgba(247,244,238,0.25)",
          backgroundColor: hover && isWeb ? STUDENT_THEME.surfaceDividerFaint : "transparent",
        },
        isWeb ? ({ cursor: "pointer", transition: "border-color 0.15s, background-color 0.15s" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 11,
          letterSpacing: 1.76,
          textTransform: "uppercase",
          color: COLORS.LINEN.hex,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Program card (lighter style)
// ────────────────────────────────────────────────────────────────────────────

function ProgramCard({ row, joined }: { row: StudentCaseRow; joined: boolean }) {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);
  const synopsis =
    row.short_description?.trim() ||
    row.brief?.split(/\r?\n/)[0]?.trim() ||
    "";
  const tier = competitionTier(row);
  const closesLabel = row.closes_at ? formatDateShort(row.closes_at) : "TBD";
  const isClosed = row.status === "closed";

  return (
    <Pressable
      onPress={() => router.push(`/student/find/competition/${row.id}` as Href)}
      accessibilityRole="button"
      accessibilityLabel={`Open ${row.name}`}
      {...(isWeb
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
        : {})}
      style={[
        {
          backgroundColor: COLORS.LINEN.hex,
          borderRadius: 8,
          borderWidth: 1,
          borderColor:
            hover && isWeb ? STUDENT_THEME.accentBorderHover : STUDENT_THEME.border,
          paddingVertical: 22,
          paddingHorizontal: 24,
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
              boxShadow:
                hover && isWeb
                  ? "0 14px 36px -12px rgba(0,0,0,0.42), 0 2px 6px rgba(0,0,0,0.20)"
                  : "0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.10)",
              transform: [{ translateY: hover ? -1 : 0 }],
            } as object)
          : null,
      ]}
    >
      {/* Top row: sponsor overline + chip */}
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONTS.bodyBold,
            fontSize: 9.5,
            letterSpacing: 1.7,
            textTransform: "uppercase",
            color: COLORS.AMBER.hex,
          }}
        >
          {row.company_name || "Sponsor"}
        </Text>
        <ProgramChip joined={joined} closed={isClosed} />
      </View>

      <Text
        numberOfLines={2}
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          lineHeight: 26,
          color: COLORS.FOREST.hex,
          marginBottom: 6,
        }}
      >
        {row.name}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 12.5,
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: synopsis ? 14 : 18,
        }}
        numberOfLines={1}
      >
        {competitionTierLabel(tier)}
      </Text>

      {synopsis ? (
        <Text
          numberOfLines={3}
          style={{
            fontFamily: FONTS.body,
            fontSize: 12.5,
            lineHeight: 19,
            color: STUDENT_THEME.textOnSurfaceDim,
            marginBottom: 16,
          }}
        >
          {synopsis}
        </Text>
      ) : null}

      {/* Footer — closes left, view-program link right */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 14,
          borderTopWidth: 1,
          borderTopColor: COLORS.SAND_LINE.hex,
        }}
      >
        <View>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: STUDENT_THEME.textOnSurfaceFaint,
            }}
          >
            Closes
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontFamily: FONTS.displayMedium,
              fontSize: 16,
              color: COLORS.FOREST.hex,
              fontVariant: ["tabular-nums"],
            }}
          >
            {closesLabel}
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10.5,
              letterSpacing: 1.68,
              textTransform: "uppercase",
              color: COLORS.AMBER_DEEP.hex,
            }}
          >
            View program
          </Text>
          <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
            <Path
              d="M2.5 6h7M6.5 3l3 3-3 3"
              stroke={COLORS.AMBER_DEEP.hex}
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      </View>
    </Pressable>
  );
}

function ProgramChip({ joined, closed }: { joined: boolean; closed: boolean }) {
  if (joined) {
    return (
      <Pill
        label="Joined"
        color={COLORS.SAGE_DEEP.hex}
        bg={STUDENT_THEME.successTintSoft}
        border={STUDENT_THEME.successBorderSoft}
        dot
      />
    );
  }
  if (closed) {
    return (
      <Pill
        label="Closed"
        color={STUDENT_THEME.textOnSurfaceDim}
        bg="rgba(28,43,26,0.06)"
        border="rgba(28,43,26,0.14)"
      />
    );
  }
  return (
    <Pill
      label="Open"
      color={STUDENT_THEME.textOnSurfaceDim}
      bg="rgba(28,43,26,0.06)"
      border="rgba(28,43,26,0.14)"
    />
  );
}

function Pill({
  label,
  color,
  bg,
  border,
  dot = false,
}: {
  label: string;
  color: string;
  bg: string;
  border: string;
  dot?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: 4,
        borderRadius: 999,
        backgroundColor: bg,
        borderWidth: 1,
        borderColor: border,
      }}
    >
      {dot ? (
        <View
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            backgroundColor: color,
          }}
        />
      ) : null}
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section label + skeleton
// ────────────────────────────────────────────────────────────────────────────

function SectionLabel({ title, count }: { title: string; count?: number }) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 2.1,
          textTransform: "uppercase",
          // Cream-muted on the forest section bg.
          color: "rgba(247,244,238,0.62)",
        }}
      >
        {title}
      </Text>
      {count != null && count > 0 ? (
        <Text
          style={{
            fontFamily: FONTS.displayMedium,
            fontSize: 14,
            color: COLORS.AMBER.hex,
          }}
        >
          {count}
        </Text>
      ) : null}
    </View>
  );
}

function SkeletonGrid({ wide }: { wide: boolean }) {
  return (
    <View>
      <Skeleton width={"100%" as unknown as number} height={220} radius={12} />
      <View style={{ height: 24 }} />
      <Skeleton width={140} height={11} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 14, marginTop: 14 }}>
        {[0, 1, 2, 3].map((k) => (
          <View
            key={k}
            style={{
              flexBasis: 0,
              flexGrow: 1,
              flexShrink: 1,
              minWidth: wide ? 320 : 280,
              width: "100%" as unknown as number,
              backgroundColor: COLORS.LINEN.hex,
              borderWidth: 1,
              borderColor: STUDENT_THEME.border,
              borderRadius: 8,
              padding: 22,
            }}
          >
            <Skeleton width={90} height={9} />
            <Skeleton width={"75%" as unknown as number} height={20} style={{ marginTop: 12 }} />
            <Skeleton width={"55%" as unknown as number} height={11} style={{ marginTop: 8 }} />
            <Skeleton width={"95%" as unknown as number} height={11} style={{ marginTop: 12 }} />
            <View style={{ marginTop: 18, flexDirection: "row", justifyContent: "space-between" }}>
              <Skeleton width={70} height={32} />
              <Skeleton width={90} height={11} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}
